import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import {
	ACTION_TYPES,
	useAppContext,
	useFeature,
} from '../../Context/AppContext';
import { useRealtimeEvent } from '../../Context/RealtimeContext';
import {
	approveTimeEntry,
	clockIn,
	clockOut,
	fetchFailedClockInAttempts,
	fetchTimeEntries,
	fetchTimeclockStatus,
	forgottenClockOut,
	rejectTimeEntry,
	updateCurrentUser,
	updateTimeEntry,
} from '../../Shared/Api';
import { getBrowserLocation } from '../../Shared/geolocation';
import { markProOnboardingStepSeen } from '../../Shared/onboarding';
import { surfaceCard } from '../../Shared/surfaceCard';

const STATUS_LABELS = {
	open: 'Clocked in',
	pending_approval: 'Pending approval',
	approved: 'Approved',
	rejected: 'Rejected',
};

const STATUS_COLORS = {
	open: 'info',
	pending_approval: 'warning',
	approved: 'success',
	rejected: 'default',
};

const formatWhen = (iso) => {
	if (!iso) return '';
	return dayjs(iso).format('MMM D, YYYY h:mm A');
};

const formatDuration = (hours) => {
	if (hours == null) return '';
	return `${Number(hours).toFixed(2)} hrs`;
};

const PAGE_SIZE = 25;

const TimePage = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { state, dispatch } = useAppContext();
	const { enqueueSnackbar } = useSnackbar();
	const hasTimeTracking = useFeature('time_tracking');
	const isManager =
		state.user.role === 'Manager' || Boolean(state.user.isSuperuser);

	const [status, setStatus] = useState({ open_entry: null, clocked_in: [] });
	const [entries, setEntries] = useState([]);
	const [pendingEntries, setPendingEntries] = useState([]);
	const [failedAttempts, setFailedAttempts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [statusFilter, setStatusFilter] = useState('');
	const [userFilter, setUserFilter] = useState('');
	const [dateFrom, setDateFrom] = useState(() => dayjs().subtract(29, 'day'));
	const [dateTo, setDateTo] = useState(() => dayjs());
	const [page, setPage] = useState(0);
	const [showFailed, setShowFailed] = useState(false);
	const [forgottenOpen, setForgottenOpen] = useState(false);
	const [forgottenReason, setForgottenReason] = useState('');
	const [forgottenAt, setForgottenAt] = useState(() =>
		dayjs().format('YYYY-MM-DDTHH:mm'),
	);
	const [editEntry, setEditEntry] = useState(null);
	const [editForm, setEditForm] = useState({
		clock_in_at: '',
		clock_out_at: '',
		notes: '',
		manager_note: '',
	});
	const [approveEntry, setApproveEntry] = useState(null);
	const [approveOut, setApproveOut] = useState('');
	const [approveNote, setApproveNote] = useState('');
	const [emailSaving, setEmailSaving] = useState(false);

	const weeklyEmailEnabled = state.user.weeklyHoursEmailEnabled !== false;
	// Stable reference — `maxDate={dayjs()}` recreates every render and can
	// infinite-loop MUI X DatePicker (white screen / max update depth).
	const today = useMemo(() => dayjs().endOf('day'), []);

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Time' },
		});
		return () => {
			dispatch({
				type: ACTION_TYPES.SET_PAGE_TITLE,
				payload: { pageTitle: '' },
			});
		};
	}, [dispatch]);

	useEffect(() => {
		if (isManager && hasTimeTracking) {
			markProOnboardingStepSeen('time');
		}
	}, [isManager, hasTimeTracking]);

	const loadAll = useCallback(async () => {
		if (!hasTimeTracking) {
			setLoading(false);
			return;
		}
		setLoading(true);
		try {
			const params = {
				start: dateFrom.startOf('day').toISOString(),
				end: dateTo.endOf('day').toISOString(),
			};
			if (statusFilter) params.status = statusFilter;
			if (isManager && userFilter) params.user = userFilter;
			const listRequests = [fetchTimeclockStatus(), fetchTimeEntries(params)];
			if (isManager) {
				listRequests.push(fetchTimeEntries({ status: 'pending_approval' }));
			}
			const [statusData, entryData, pendingData] =
				await Promise.all(listRequests);
			setStatus(statusData || { open_entry: null, clocked_in: [] });
			const nextEntries = Array.isArray(entryData)
				? entryData
				: Array.isArray(entryData?.results)
					? entryData.results
					: [];
			setEntries(nextEntries);
			if (isManager) {
				const nextPending = Array.isArray(pendingData)
					? pendingData
					: Array.isArray(pendingData?.results)
						? pendingData.results
						: [];
				setPendingEntries(nextPending);
			} else {
				setPendingEntries([]);
			}
			setPage(0);
			if (isManager) {
				try {
					const attempts = await fetchFailedClockInAttempts();
					setFailedAttempts(Array.isArray(attempts) ? attempts : []);
				} catch {
					setFailedAttempts([]);
				}
			}
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to load time data.', {
				variant: 'error',
			});
		} finally {
			setLoading(false);
		}
	}, [
		dateFrom,
		dateTo,
		enqueueSnackbar,
		hasTimeTracking,
		isManager,
		statusFilter,
		userFilter,
	]);

	useEffect(() => {
		loadAll();
	}, [loadAll]);

	const handleRealtime = useCallback(
		(message) => {
			if (message?.type === 'timeclock.status_updated') {
				loadAll();
			}
		},
		[loadAll],
	);
	useRealtimeEvent(handleRealtime);

	const handleClockIn = async () => {
		setActionLoading(true);
		try {
			let payload = {};
			try {
				payload = await getBrowserLocation();
			} catch (locError) {
				// Retry without location; API enforces org policy.
				enqueueSnackbar(locError.message, { variant: 'warning' });
			}
			await clockIn(payload);
			enqueueSnackbar('Clocked in.', { variant: 'success' });
			await loadAll();
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not clock in.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(false);
		}
	};

	const handleClockOut = async () => {
		setActionLoading(true);
		try {
			let payload = {};
			try {
				payload = await getBrowserLocation();
			} catch (locError) {
				enqueueSnackbar(locError.message, { variant: 'warning' });
			}
			await clockOut(payload);
			enqueueSnackbar('Clocked out.', { variant: 'success' });
			await loadAll();
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not clock out.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(false);
		}
	};

	const handleForgottenSubmit = async () => {
		if (!forgottenReason.trim()) {
			enqueueSnackbar('A reason is required.', { variant: 'error' });
			return;
		}
		setActionLoading(true);
		try {
			await forgottenClockOut({
				clock_out_at: dayjs(forgottenAt).toISOString(),
				reason: forgottenReason.trim(),
			});
			enqueueSnackbar('Submitted for manager approval.', {
				variant: 'success',
			});
			setForgottenOpen(false);
			setForgottenReason('');
			await loadAll();
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not submit.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(false);
		}
	};

	const openEdit = (entry) => {
		setEditEntry(entry);
		setEditForm({
			clock_in_at: entry.clock_in_at
				? dayjs(entry.clock_in_at).format('YYYY-MM-DDTHH:mm')
				: '',
			clock_out_at: entry.clock_out_at
				? dayjs(entry.clock_out_at).format('YYYY-MM-DDTHH:mm')
				: '',
			notes: entry.notes || '',
			manager_note: entry.manager_note || '',
		});
	};

	const handleEditSave = async () => {
		if (!editEntry) return;
		setActionLoading(true);
		try {
			await updateTimeEntry(editEntry.id, {
				clock_in_at: dayjs(editForm.clock_in_at).toISOString(),
				clock_out_at: editForm.clock_out_at
					? dayjs(editForm.clock_out_at).toISOString()
					: null,
				notes: editForm.notes,
				manager_note: editForm.manager_note,
			});
			enqueueSnackbar('Entry updated.', { variant: 'success' });
			setEditEntry(null);
			await loadAll();
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not update entry.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(false);
		}
	};

	const handleApprove = async () => {
		if (!approveEntry) return;
		setActionLoading(true);
		try {
			const body = { manager_note: approveNote };
			if (approveOut) {
				body.clock_out_at = dayjs(approveOut).toISOString();
			}
			await approveTimeEntry(approveEntry.id, body);
			enqueueSnackbar('Entry approved.', { variant: 'success' });
			setApproveEntry(null);
			await loadAll();
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not approve.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(false);
		}
	};

	const handleReject = async (entry) => {
		setActionLoading(true);
		try {
			await rejectTimeEntry(entry.id, {
				manager_note: 'Rejected by manager',
			});
			enqueueSnackbar('Entry rejected.', { variant: 'info' });
			await loadAll();
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not reject.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(false);
		}
	};

	const handleEmailToggle = async (event) => {
		const enabled = event.target.checked;
		setEmailSaving(true);
		try {
			const updated = await updateCurrentUser({
				weekly_hours_email_enabled: enabled,
			});
			dispatch({
				type: ACTION_TYPES.UPDATE_USER,
				payload: updated,
			});
			enqueueSnackbar(
				enabled
					? 'Weekly hours email enabled.'
					: 'Weekly hours email disabled.',
				{ variant: 'success' },
			);
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not update preference.', {
				variant: 'error',
			});
		} finally {
			setEmailSaving(false);
		}
	};

	const filterUsers = useMemo(() => {
		const map = new Map();
		entries.forEach((e) => {
			if (e.user != null) {
				map.set(e.user, e.user_name || `User ${e.user}`);
			}
		});
		pendingEntries.forEach((e) => {
			if (e.user != null) {
				map.set(e.user, e.user_name || `User ${e.user}`);
			}
		});
		(status.clocked_in || []).forEach((e) => {
			map.set(e.user, e.user_name || `User ${e.user}`);
		});
		return Array.from(map.entries());
	}, [entries, pendingEntries, status.clocked_in]);

	const pagedEntries = useMemo(() => {
		const start = page * PAGE_SIZE;
		return entries.slice(start, start + PAGE_SIZE);
	}, [entries, page]);

	if (!hasTimeTracking) {
		return (
			<Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720 }}>
				<Alert severity="info">
					Time tracking is available on the Pro plan. Upgrade in Settings -
					Billing to clock in and manage timesheets.
				</Alert>
			</Box>
		);
	}

	const openEntry = status.open_entry;

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
				<Stack spacing={3}>
					{/* Clock widget */}
					<Box sx={{ ...surfaceCard(theme), p: { xs: 2.5, md: 3 } }}>
						<Stack
							direction={{ xs: 'column', sm: 'row' }}
							spacing={2}
							alignItems={{ sm: 'center' }}
							justifyContent="space-between"
						>
							<Stack direction="row" spacing={1.5} alignItems="center">
								<AccessTimeIcon color="primary" sx={{ fontSize: 36 }} />
								<Box>
									<Typography variant="h5" fontWeight={700}>
										{openEntry ? 'You are clocked in' : 'You are clocked out'}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{openEntry
											? `Since ${formatWhen(openEntry.clock_in_at)}`
											: 'Clock in when you start work'}
									</Typography>
								</Box>
							</Stack>
							<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
								{openEntry ? (
									<>
										<Button
											variant="contained"
											color="primary"
											startIcon={<LogoutIcon />}
											onClick={handleClockOut}
											disabled={actionLoading}
										>
											Clock out
										</Button>
										<Button
											variant="outlined"
											onClick={() => setForgottenOpen(true)}
											disabled={actionLoading}
										>
											Forgot to clock out
										</Button>
									</>
								) : (
									<Button
										variant="contained"
										color="primary"
										startIcon={<LoginIcon />}
										onClick={handleClockIn}
										disabled={actionLoading}
										size="large"
									>
										Clock in
									</Button>
								)}
							</Stack>
						</Stack>
						{actionLoading && (
							<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
								<CircularProgress size={24} />
							</Box>
						)}
					</Box>

					{/* Manager: who's clocked in */}
					{isManager && (
						<Box sx={{ ...surfaceCard(theme), p: { xs: 2.5, md: 3 } }}>
							<Typography variant="h6" fontWeight={700} gutterBottom>
								Who&apos;s clocked in
							</Typography>
							{(status.clocked_in || []).length === 0 ? (
								<Typography variant="body2" color="text.secondary">
									Nobody is clocked in right now.
								</Typography>
							) : (
								<Stack spacing={1}>
									{(status.clocked_in || []).map((row) => (
										<Stack
											key={row.id}
											direction="row"
											justifyContent="space-between"
											alignItems="center"
											sx={{
												py: 1,
												borderBottom: 1,
												borderColor: 'divider',
											}}
										>
											<Typography fontWeight={600}>{row.user_name}</Typography>
											<Typography variant="body2" color="text.secondary">
												since {formatWhen(row.clock_in_at)}
											</Typography>
										</Stack>
									))}
								</Stack>
							)}
						</Box>
					)}

					{/* Manager: pending approvals */}
					{isManager && pendingEntries.length > 0 && (
						<Box sx={{ ...surfaceCard(theme), p: { xs: 2.5, md: 3 } }}>
							<Typography variant="h6" fontWeight={700} gutterBottom>
								Pending approvals
							</Typography>
							<Stack spacing={2}>
								{pendingEntries.map((entry) => (
									<Box
										key={entry.id}
										sx={{
											p: 2,
											borderRadius: 2,
											bgcolor: 'action.hover',
										}}
									>
										<Typography fontWeight={700}>{entry.user_name}</Typography>
										<Typography variant="body2" color="text.secondary">
											In {formatWhen(entry.clock_in_at)} - proposed out{' '}
											{formatWhen(
												entry.proposed_clock_out_at || entry.clock_out_at,
											)}
										</Typography>
										<Typography variant="body2" sx={{ mt: 1 }}>
											Reason: {entry.forgotten_clock_out_reason || ''}
										</Typography>
										<Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
											<Button
												size="small"
												variant="contained"
												onClick={() => {
													setApproveEntry(entry);
													setApproveOut(
														entry.clock_out_at
															? dayjs(entry.clock_out_at).format(
																	'YYYY-MM-DDTHH:mm',
																)
															: '',
													);
													setApproveNote('');
												}}
											>
												Approve
											</Button>
											<Button
												size="small"
												color="inherit"
												onClick={() => handleReject(entry)}
												disabled={actionLoading}
											>
												Reject
											</Button>
										</Stack>
									</Box>
								))}
							</Stack>
						</Box>
					)}

					{/* Timesheet */}
					<Box sx={{ ...surfaceCard(theme), p: { xs: 2.5, md: 3 } }}>
						<Stack spacing={2} sx={{ mb: 2 }}>
							<Typography variant="h6" fontWeight={700}>
								{isManager ? 'Timesheets' : 'My timesheet'}
							</Typography>
							<Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
								<DatePicker
									label="From"
									value={dateFrom}
									onChange={(value) => value && setDateFrom(value)}
									maxDate={dateTo}
									slotProps={{
										textField: { size: 'small', sx: { minWidth: 150 } },
									}}
								/>
								<DatePicker
									label="To"
									value={dateTo}
									onChange={(value) => value && setDateTo(value)}
									minDate={dateFrom}
									maxDate={today}
									slotProps={{
										textField: { size: 'small', sx: { minWidth: 150 } },
									}}
								/>
								<Button
									size="small"
									variant="outlined"
									onClick={() => {
										setDateFrom(dayjs().subtract(29, 'day'));
										setDateTo(dayjs());
									}}
								>
									Last 30 days
								</Button>
								<Button
									size="small"
									variant="outlined"
									onClick={() => {
										const today = dayjs();
										const weekday = today.day();
										const diff = weekday === 0 ? -6 : 1 - weekday;
										setDateFrom(today.add(diff, 'day').startOf('day'));
										setDateTo(today);
									}}
								>
									This week
								</Button>
								<FormControl size="small" sx={{ minWidth: 140 }}>
									<InputLabel>Status</InputLabel>
									<Select
										label="Status"
										value={statusFilter}
										onChange={(e) => setStatusFilter(e.target.value)}
									>
										<MenuItem value="">All</MenuItem>
										<MenuItem value="open">Open</MenuItem>
										<MenuItem value="pending_approval">Pending</MenuItem>
										<MenuItem value="approved">Approved</MenuItem>
										<MenuItem value="rejected">Rejected</MenuItem>
									</Select>
								</FormControl>
								{isManager && (
									<FormControl size="small" sx={{ minWidth: 160 }}>
										<InputLabel>Employee</InputLabel>
										<Select
											label="Employee"
											value={userFilter}
											onChange={(e) => setUserFilter(e.target.value)}
										>
											<MenuItem value="">Everyone</MenuItem>
											{filterUsers.map(([id, name]) => (
												<MenuItem key={id} value={String(id)}>
													{name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								)}
							</Stack>
						</Stack>

						{loading ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
								<CircularProgress />
							</Box>
						) : entries.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								No time entries in this date range.
							</Typography>
						) : (
							<>
								{isMobile ? (
									<Stack spacing={1.5} divider={<Divider />}>
										{pagedEntries.map((entry) => (
											<Box key={entry.id}>
												{isManager && (
													<Typography fontWeight={600}>
														{entry.user_name}
													</Typography>
												)}
												<Typography variant="body2">
													{formatWhen(entry.clock_in_at)}
													{entry.clock_out_at
														? ` - ${formatWhen(entry.clock_out_at)}`
														: ''}
												</Typography>
												<Stack
													direction="row"
													spacing={1}
													alignItems="center"
													sx={{ mt: 0.5 }}
												>
													<Chip
														size="small"
														label={STATUS_LABELS[entry.status] || entry.status}
														color={STATUS_COLORS[entry.status] || 'default'}
													/>
													<Typography variant="body2">
														{formatDuration(entry.duration_hours)}
													</Typography>
												</Stack>
												{isManager && (
													<Button
														size="small"
														sx={{ mt: 1 }}
														onClick={() => openEdit(entry)}
													>
														Edit
													</Button>
												)}
											</Box>
										))}
									</Stack>
								) : (
									<Table size="small">
										<TableHead>
											<TableRow>
												{isManager && <TableCell>Employee</TableCell>}
												<TableCell>Clock in</TableCell>
												<TableCell>Clock out</TableCell>
												<TableCell>Hours</TableCell>
												<TableCell>Status</TableCell>
												{isManager && (
													<TableCell align="right">Actions</TableCell>
												)}
											</TableRow>
										</TableHead>
										<TableBody>
											{pagedEntries.map((entry) => (
												<TableRow key={entry.id} hover>
													{isManager && (
														<TableCell>{entry.user_name}</TableCell>
													)}
													<TableCell>{formatWhen(entry.clock_in_at)}</TableCell>
													<TableCell>
														{formatWhen(entry.clock_out_at)}
													</TableCell>
													<TableCell>
														{formatDuration(entry.duration_hours)}
													</TableCell>
													<TableCell>
														<Chip
															size="small"
															label={
																STATUS_LABELS[entry.status] || entry.status
															}
															color={STATUS_COLORS[entry.status] || 'default'}
														/>
													</TableCell>
													{isManager && (
														<TableCell align="right">
															<Button
																size="small"
																onClick={() => openEdit(entry)}
															>
																Edit
															</Button>
														</TableCell>
													)}
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
								<TablePagination
									component="div"
									count={entries.length}
									page={page}
									onPageChange={(_, next) => setPage(next)}
									rowsPerPage={PAGE_SIZE}
									rowsPerPageOptions={[PAGE_SIZE]}
									onRowsPerPageChange={() => {}}
									labelDisplayedRows={({ from, to, count }) =>
										`${from}-${to} of ${count}`
									}
								/>
							</>
						)}
					</Box>

					{/* Weekly email preference */}
					<Box sx={{ ...surfaceCard(theme), p: { xs: 2.5, md: 3 } }}>
						<FormControlLabel
							control={
								<Switch
									checked={weeklyEmailEnabled}
									onChange={handleEmailToggle}
									disabled={emailSaving}
								/>
							}
							label="Email me a weekly hours report"
						/>
						<Typography variant="body2" color="text.secondary">
							Sends a summary of your previous week&apos;s hours every Monday
							(default on). Managers also receive the full team summary.
						</Typography>
					</Box>

					{/* Failed geofence attempts */}
					{isManager && (
						<Box sx={{ ...surfaceCard(theme), p: { xs: 2.5, md: 3 } }}>
							<Button
								onClick={() => setShowFailed((v) => !v)}
								endIcon={showFailed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								sx={{ textTransform: 'none', fontWeight: 700 }}
							>
								Failed clock-in attempts ({failedAttempts.length})
							</Button>
							<Collapse in={showFailed}>
								{failedAttempts.length === 0 ? (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mt: 1 }}
									>
										No failed attempts logged.
									</Typography>
								) : (
									<Stack spacing={1} sx={{ mt: 1.5 }}>
										{failedAttempts.map((attempt) => (
											<Typography key={attempt.id} variant="body2">
												<strong>{attempt.user_name}</strong> {attempt.reason}
												{attempt.distance_feet != null
													? ` (${attempt.distance_feet} ft)`
													: ''}{' '}
												{formatWhen(attempt.attempted_at)}
											</Typography>
										))}
									</Stack>
								)}
							</Collapse>
						</Box>
					)}
				</Stack>

				{/* Forgotten clock-out dialog */}
				<Dialog
					open={forgottenOpen}
					onClose={() => setForgottenOpen(false)}
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle>Forgot to clock out</DialogTitle>
					<DialogContent>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Propose when you actually left. A manager must approve before
							these hours count as finalized.
						</Typography>
						<Stack spacing={2}>
							<TextField
								label="Clock-out time"
								type="datetime-local"
								value={forgottenAt}
								onChange={(e) => setForgottenAt(e.target.value)}
								InputLabelProps={{ shrink: true }}
								fullWidth
							/>
							<TextField
								label="Reason"
								value={forgottenReason}
								onChange={(e) => setForgottenReason(e.target.value)}
								multiline
								minRows={3}
								required
								fullWidth
								placeholder="Why you forgot to clock out"
							/>
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setForgottenOpen(false)}>Cancel</Button>
						<Button
							variant="contained"
							onClick={handleForgottenSubmit}
							disabled={actionLoading}
						>
							Submit for approval
						</Button>
					</DialogActions>
				</Dialog>

				{/* Edit dialog */}
				<Dialog
					open={Boolean(editEntry)}
					onClose={() => setEditEntry(null)}
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle>Edit time entry</DialogTitle>
					<DialogContent>
						<Stack spacing={2} sx={{ mt: 1 }}>
							<TextField
								label="Clock in"
								type="datetime-local"
								value={editForm.clock_in_at}
								onChange={(e) =>
									setEditForm((prev) => ({
										...prev,
										clock_in_at: e.target.value,
									}))
								}
								InputLabelProps={{ shrink: true }}
								fullWidth
							/>
							<TextField
								label="Clock out"
								type="datetime-local"
								value={editForm.clock_out_at}
								onChange={(e) =>
									setEditForm((prev) => ({
										...prev,
										clock_out_at: e.target.value,
									}))
								}
								InputLabelProps={{ shrink: true }}
								fullWidth
							/>
							<TextField
								label="Notes"
								value={editForm.notes}
								onChange={(e) =>
									setEditForm((prev) => ({ ...prev, notes: e.target.value }))
								}
								fullWidth
								multiline
								minRows={2}
							/>
							<TextField
								label="Manager note"
								value={editForm.manager_note}
								onChange={(e) =>
									setEditForm((prev) => ({
										...prev,
										manager_note: e.target.value,
									}))
								}
								fullWidth
								multiline
								minRows={2}
							/>
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setEditEntry(null)}>Cancel</Button>
						<Button
							variant="contained"
							onClick={handleEditSave}
							disabled={actionLoading}
						>
							Save
						</Button>
					</DialogActions>
				</Dialog>

				{/* Approve dialog */}
				<Dialog
					open={Boolean(approveEntry)}
					onClose={() => setApproveEntry(null)}
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle>Approve forgotten clock-out</DialogTitle>
					<DialogContent>
						<Stack spacing={2} sx={{ mt: 1 }}>
							<TextField
								label="Clock-out time"
								type="datetime-local"
								value={approveOut}
								onChange={(e) => setApproveOut(e.target.value)}
								InputLabelProps={{ shrink: true }}
								fullWidth
								helperText="Adjust if needed, or leave as submitted"
							/>
							<TextField
								label="Manager note"
								value={approveNote}
								onChange={(e) => setApproveNote(e.target.value)}
								fullWidth
								multiline
								minRows={2}
							/>
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setApproveEntry(null)}>Cancel</Button>
						<Button
							variant="contained"
							onClick={handleApprove}
							disabled={actionLoading}
						>
							Approve
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</LocalizationProvider>
	);
};

export default TimePage;
